<template>
  <div
    v-show="!(isErrorNoDisplay && loadingStateValue === -1)"
    class="base-thumbnail"
    :class="[
      {
        'page-hina': isHina,
        'page-jps': isJps,
        'has-hover-scale': appearance && appearance?.hoverScale,
      },
      appearance && `radius-${appearance?.borderRadius}`,
      loadingStateValue !== 1 && 'is-no-image'
    ]"
  >
    <div class="base-thumbnail-inner hover-target">
      <slot>
        <BaseImage
          :src="src"
          :w="w"
          :h="h"
          :natural="natural"
          :cover="cover"
          :background="background"
          :alt="alt"
          :error-alt="errorAlt"
          :is-error-no-display="isErrorNoDisplay"
          @stateChanged="(state) => loadingStateValue = state"
        />
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
export interface ThumbnailAppearance {
  borderRadius?: 'small' | 'medium' | 'large' | 'custom'
  hoverScale?: boolean
}

type Props = {
  src?: string
  w?: number
  h?: number
  natural?: boolean
  cover?: boolean
  background?: string
  alt?: string
  errorAlt?: string
  isErrorNoDisplay?: boolean
  appearance?: ThumbnailAppearance
  modelValue?: -1 | 0 | 1  // 利用側で slot を上書きする場合は必須
}

type Emits = {
  (e: 'update:modelValue', value: Props['modelValue']): void
}

const props = withDefaults(defineProps<Props>(), {})
const emits = defineEmits<Emits>()

const { isHina, isJps } = useServiceType()

/**
 * 0:読み込み中
 * 1:読み込み完了
 * -1:読み込みエラー
 */
const internalLoadingState: Ref<number> = ref(0)
const loadingStateValue = computed({
  get: () => {
    return props.modelValue === undefined
      ? internalLoadingState.value
      : props.modelValue
  },
  set: (newValue: Props['modelValue']) => {
    internalLoadingState.value = newValue ?? 0
    emits('update:modelValue', newValue ?? 0)
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.base-thumbnail {
  overflow: hidden;

  // 角丸
  &.radius-small {
    border-radius: 4px;
  }

  &.radius-medium {
    border-radius: 6px;
  }

  &.radius-large {
    border-radius: 16px;
  }

  &.radius-custom {}

  // マウスオーバー
  &.has-hover-scale {
    @include is-tablet {
      .hover-target {
        @include transition-transform();
      }

      &:hover .hover-target {
        transform: scale(1.05);
      }
    }
  }

  .base-thumbnail-inner {
    width: 100%;
    height: 100%;
  }
}

.page-hina.base-thumbnail {
  border: 1px solid $hina-separator-dark;
  background-color: $white;

  &.is-no-image {
    background-color: $hina-background-cream;
  }
}
</style>
